import { ID } from '@rossum/api-client';
import { SchemaField } from '@rossum/api-client/schemaFields';
import { SchemaColumn } from '@rossum/api-client/shared';
import {
  dateOperators,
  numericOperators,
  stringFieldOperators,
} from '../mql/operators';
import {
  RenderDatapointField,
  RenderDatapointFieldRow,
} from './components/RenderDatapointField';
import { useFetchSchemaFields } from './hooks/useFetchSchemaFields';
import { SchemaColDef } from './TypedGridColDef';
import { getColumnField } from './utils';

const setLabelForSchemaField =
  (schemaFields: SchemaField[]) => (schemaColumn: SchemaColumn) => {
    const columnLabelInSchema = schemaFields.find(
      field =>
        field.schemaId === schemaColumn.schemaId &&
        field.type === schemaColumn.dataType
    )?.label;

    return {
      ...schemaColumn,
      label: columnLabelInSchema ?? schemaColumn.schemaId,
    };
  };

export type SchemaColumnBase = RenderDatapointFieldRow;

export const useSchemaColumns = <RowType extends SchemaColumnBase>({
  existingSchemaColumns,
  activeQueueId,
}: {
  existingSchemaColumns: SchemaColumn[];
  activeQueueId: ID | null;
}): {
  columns: SchemaColDef<RowType>[];
  isLoading: boolean;
} => {
  const { data: schemaFields, isLoading: schemaFieldsLoading } =
    useFetchSchemaFields({
      schemaIds: existingSchemaColumns.map(schemaCol => schemaCol.schemaId),
      queueIds: activeQueueId ? [activeQueueId] : [],
    });

  // we don't store schema column labels (displayed in table header)
  // So here; we fetch the schema fields separately, find the corresponding label there, and append it to the column here
  const schemaColumnsWithLabels = existingSchemaColumns.map(
    setLabelForSchemaField(schemaFields?.results ?? [])
  );

  const schemaColumns = schemaColumnsWithLabels.map<SchemaColDef<RowType>>(
    c => {
      const schemaOperators =
        c.dataType === 'date'
          ? dateOperators
          : c.dataType === 'number'
            ? numericOperators
            : stringFieldOperators;

      return {
        align: 'left',
        columnType: 'schema',
        schemaId: c.schemaId,
        dataType: c.dataType,
        headerAlign: 'left',
        visible: c.visible,
        scoreThreshold: c.scoreThreshold,
        field: getColumnField({
          columnType: c.columnType,
          field: c.schemaId,
          dataType: c.dataType,
        }),
        headerName: c.label,
        renderCell: params => <RenderDatapointField {...params} />,
        width: c.width ?? 170,
        // Using `date` type here will cause MUI Data Grid to throw an error: `date` column type only accepts `Date` objects as values.
        // do not set `date` type as column value doesn't need to be in parseable format and easily convertable to Date object
        // but use date filter operators that render Datepicker
        type: c.dataType === 'date' ? 'string' : c.dataType,
        operators: schemaOperators,
        filterable: true,
      };
    }
  );

  return {
    columns: schemaColumns,
    isLoading: schemaFieldsLoading && existingSchemaColumns.length > 0,
  };
};
